import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { VoiceAudioEngine } from '../services/voiceAudioEngine';
import { VoiceSocketClient } from '../services/voiceSocketClient';

function PatientVoice() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, ready, connecting, listening, ended, error
  const [sessionInfo, setSessionInfo] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);

  const audioEngineRef = useRef(null);
  const socketClientRef = useRef(null);
  const timerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Validate token on mount
  useEffect(() => {
    const validate = async () => {
      try {
        const serverUrl = window.location.origin;
        const res = await fetch(`${serverUrl}/api/voice/validate-token/${token}`);
        const data = await res.json();
        if (!data.success) {
          setErrorMsg(data.error || 'Invalid or expired link');
          setStatus('error');
          return;
        }
        setSessionInfo(data.data);
        setStatus('ready');
      } catch (err) {
        setErrorMsg('Unable to verify link. Please check your connection.');
        setStatus('error');
      }
    };
    validate();
  }, [token]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  // Duration timer
  useEffect(() => {
    if (status === 'listening') {
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status]);

  const startSession = async () => {
    setStatus('connecting');

    const engine = new VoiceAudioEngine();
    const client = new VoiceSocketClient();
    audioEngineRef.current = engine;
    socketClientRef.current = client;

    client.on('connect', () => {
      client.initializeConnection({
        practiceId: sessionInfo.practiceId,
        patientId: sessionInfo.patientId,
        campaignType: sessionInfo.campaignType
      });
    });

    client.on('audioReady', async () => {
      client.promptStart();
      try {
        await engine.startCapture((base64Audio) => {
          if (client.isConnected) client.sendAudio(base64Audio);
        });
        setStatus('listening');
        client.startAudio();
      } catch (err) {
        setErrorMsg('Microphone access denied. Please allow microphone access and try again.');
        setStatus('error');
      }
    });

    client.on('audioOutput', (data) => engine.playAudioChunk(data.audio));
    client.on('textOutput', (data) => setTranscript(prev => [...prev, { role: data.role, text: data.text }]));
    client.on('sessionClosed', () => { cleanup(); setStatus('ended'); });
    client.on('error', (data) => { setErrorMsg(data.message || 'Session error'); setStatus('error'); });
    client.on('connect_error', () => { setErrorMsg('Failed to connect'); setStatus('error'); });

    client.connect({ voiceToken: token });
  };

  const endSession = () => {
    cleanup();
    setStatus('ended');
  };

  function cleanup() {
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioEngineRef.current) audioEngineRef.current.destroy();
    if (socketClientRef.current) {
      socketClientRef.current.endSession();
      socketClientRef.current.disconnect();
    }
  }

  const formatDuration = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div style={pStyles.page}>
      <div style={pStyles.card}>
        {/* Header */}
        <div style={pStyles.header}>
          <div style={pStyles.logo}>
            {sessionInfo?.practiceName || 'NewtonHealth Connect'}
          </div>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div style={pStyles.center}>
            <div style={pStyles.spinner} />
            <p style={pStyles.text}>Verifying your link...</p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div style={pStyles.center}>
            <div style={pStyles.errorIcon}>!</div>
            <h2 style={pStyles.title}>Unable to Start</h2>
            <p style={pStyles.text}>{errorMsg}</p>
          </div>
        )}

        {/* Ready to start */}
        {status === 'ready' && (
          <div style={pStyles.center}>
            <div style={pStyles.micIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </div>
            <h2 style={pStyles.title}>Hi{sessionInfo?.patientFirstName ? `, ${sessionInfo.patientFirstName}` : ''}!</h2>
            <p style={pStyles.text}>
              {sessionInfo?.practiceName} would like to speak with you. Click below to start the conversation.
            </p>
            <p style={pStyles.smallText}>
              You'll need to allow microphone access when prompted.
            </p>
            <button style={pStyles.startButton} onClick={startSession}>
              Start Conversation
            </button>
          </div>
        )}

        {/* Connecting */}
        {status === 'connecting' && (
          <div style={pStyles.center}>
            <div style={pStyles.spinner} />
            <p style={pStyles.text}>Connecting...</p>
          </div>
        )}

        {/* Active session */}
        {status === 'listening' && (
          <div style={pStyles.sessionContainer}>
            <div style={pStyles.liveBar}>
              <div style={pStyles.liveDot} />
              <span style={pStyles.liveText}>Live</span>
              <span style={pStyles.durationText}>{formatDuration(duration)}</span>
            </div>

            <div style={pStyles.transcriptArea}>
              {transcript.length === 0 && (
                <p style={pStyles.waitText}>AI is starting the conversation...</p>
              )}
              {transcript.map((entry, i) => (
                <div key={i} style={{
                  ...pStyles.bubble,
                  ...(entry.role === 'ASSISTANT' ? pStyles.bubbleAI : pStyles.bubbleUser)
                }}>
                  <span style={pStyles.bubbleRole}>
                    {entry.role === 'ASSISTANT' ? sessionInfo?.practiceName || 'AI' : 'You'}
                  </span>
                  <span>{entry.text}</span>
                </div>
              ))}
              <div ref={transcriptEndRef} />
            </div>

            <button style={pStyles.endButton} onClick={endSession}>
              End Conversation
            </button>
          </div>
        )}

        {/* Ended */}
        {status === 'ended' && (
          <div style={pStyles.center}>
            <h2 style={pStyles.title}>Conversation Ended</h2>
            <p style={pStyles.text}>
              Thank you for your time. If you need further assistance, please contact {sessionInfo?.practiceName || 'your practice'} directly.
            </p>
            {duration > 0 && (
              <p style={pStyles.smallText}>Duration: {formatDuration(duration)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const pStyles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'center'
  },
  logo: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 24px',
    textAlign: 'center'
  },
  title: {
    margin: '16px 0 8px 0',
    fontSize: '22px',
    fontWeight: '600',
    color: '#111827'
  },
  text: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    lineHeight: '1.6',
    color: '#6b7280'
  },
  smallText: {
    margin: '0 0 24px 0',
    fontSize: '13px',
    color: '#9ca3af'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: '700'
  },
  micIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#eff6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  startButton: {
    padding: '14px 32px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  sessionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '500px'
  },
  liveBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderBottom: '1px solid #e5e7eb'
  },
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#10b981'
  },
  liveText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#10b981'
  },
  durationText: {
    marginLeft: 'auto',
    fontSize: '13px',
    color: '#6b7280',
    fontVariantNumeric: 'tabular-nums'
  },
  transcriptArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  waitText: {
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '14px',
    padding: '20px 0'
  },
  bubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '10px 14px',
    borderRadius: '10px',
    maxWidth: '85%',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827'
  },
  bubbleAI: {
    backgroundColor: '#eff6ff',
    alignSelf: 'flex-start'
  },
  bubbleUser: {
    backgroundColor: '#f0fdf4',
    alignSelf: 'flex-end'
  },
  bubbleRole: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase'
  },
  endButton: {
    margin: '16px 24px',
    padding: '12px',
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

// Add spinner animation if not already present
if (typeof document !== 'undefined' && !document.getElementById('patient-voice-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'patient-voice-styles';
  styleSheet.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default PatientVoice;
